import type { BreadCrumbItem } from 'components/Breadcrumb'
import type { FC } from 'react'
import type { NavigationItemTree } from 'types/gql/navigation-item-tree'

import { GeneralError, NoCategoryError } from 'components/errors/ErrorPage'
import { useNavigation } from 'components/Header/TopNavigation/services/use-navigation'
import { CartSidebarLayout } from 'components/layout/CartSidebarLayout'
import { NextSeo } from 'next-seo'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { isNavigationItem, searchNavigationItem } from 'utils/common/search-navigation-item'

import { ProductResultsGrid } from './ProductResultsGrid'

const TEST_ID = 'product-category-page'

const customSlugs = (slugs: string[]): string[] => {
  if (slugs.length === 3) {
    return [slugs[0], `${slugs[0]}/${slugs[1]}`, `${slugs[0]}/${slugs[1]}/${slugs[2]}`]
  }
  if (slugs.length === 2) {
    return [slugs[0], `${slugs[0]}/${slugs[1]}`]
  }
  return [slugs[0]]
}

const generateBreadcrumbs = (slugs: string[], data: NavigationItemTree): BreadCrumbItem[] =>
  customSlugs(slugs)
    .map((slugItem) => searchNavigationItem(data, slugItem, 'slug'))
    .filter(isNavigationItem)
    .map((item) => ({
      id: item.id,
      name: item.name,
      slug: item.slug,
      href: '/tuotteet/[...slugs]',
      link: `/tuotteet/${item.slug}`,
    }))

export interface ProductsProps {
  page?: number
  slugs: string[]
}

export const Products: FC<ProductsProps> = ({ page, slugs }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { data: navigationData, loading: loadingNavigationData } = useNavigation()

  /** all valid category items, and the names of the two deepest */
  const [categoryItems, seoCategoryNames] = useMemo(() => {
    if (!slugs || !navigationData?.store?.navigation) return [[], []]
    const items = generateBreadcrumbs(slugs, navigationData.store.navigation)
    const names = items
      .slice(-2)
      .reverse()
      .map((item) => item.name)
    return [items, names]
  }, [navigationData?.store?.navigation, slugs])

  const seoTitlePrefix = useMemo(() => {
    if (!slugs?.length) return t`All products`
    return seoCategoryNames.join(' - ') || t`pageTitle_category-not-found`
  }, [seoCategoryNames, slugs?.length, t])

  /**
   * `document.title` has the two deepest category names:
   * @example Kaikki tuotteet | S-kaupat ruoan verkkokauppa
   * @example Kuivatuotteet ja leivonta | S-kaupat ruoan verkkokauppa
   * @example Leivontatarvikkeet - Kuivatuotteet ja leivonta | S-kaupat ruoan verkkokauppa
   */
  const seoTitle = `${seoTitlePrefix} | ${theme.seoTags.title}`

  /**
   * The `og:title` should use only the single deepest category name, and no `|`:
   * @example Kaikki tuotteet - S-kaupat ruoan verkkokauppa
   * @example Kuivatuotteet ja leivonta - S-kaupat ruoan verkkokauppa
   * @example Leivontatarvikkeet - S-kaupat ruoan verkkokauppa
   */
  const ogTitle = `${seoCategoryNames[0] || t`All products`} - ${theme.seoTags.title}`

  /**
   * Description contains the single deepest category name, in lower-case:
   * @example Tilaa ruokaostokset S-kaupoista nyt suoraan kotiovellesi toimitettuna.
   * @example Tilaa kuivatuotteet ja leivonta sekä muut ruokaostokset S-kaupoista nyt suoraan kotiovellesi toimitettuna.
   * @example Tilaa leivontatarvikkeet sekä muut ruokaostokset S-kaupoista nyt suoraan kotiovellesi toimitettuna.
   */
  const seoDescription = seoCategoryNames[0]
    ? t('Category page SEO', { categoryName: seoCategoryNames[0].toLowerCase() })
    : theme.seoTags.description

  // Error page when navigation data failed to load
  if (!loadingNavigationData && !navigationData?.store?.navigation) {
    return (
      <CartSidebarLayout data-test-id={TEST_ID}>
        <GeneralError />
      </CartSidebarLayout>
    )
  }

  // If there's 3 slugs but 2 categories, one of the is invalid like `asdf`
  if (!loadingNavigationData && slugs.length && slugs.length !== categoryItems.length) {
    return (
      <CartSidebarLayout data-test-id={TEST_ID}>
        <NextSeo title={`${t`pageTitle_category-not-found`} | ${theme.seoTags.title}`} />
        <NoCategoryError />
      </CartSidebarLayout>
    )
  }

  return (
    <>
      <NextSeo title={seoTitle} description={seoDescription} openGraph={{ title: ogTitle }} />
      <ProductResultsGrid
        categoryItems={categoryItems}
        data-test-id={TEST_ID}
        navigation={navigationData?.store?.navigation}
        page={page}
        slugs={slugs}
      />
    </>
  )
}
